// The catalogue of *real* past exam sittings the mock-exam browser lists.
//
// The question bank only holds the sittings that have been converted into
// `questions/<exam-id>/*.md` so far, but a candidate browsing for a past paper
// wants to see the whole shelf — including the sittings that exist and haven't
// been added yet, so they read as "not here yet" rather than "never happened".
// `lib/pastExams.ts` merges this catalogue with whatever the bank actually
// holds; a sitting present in the bank but missing here still shows up, so
// importing a new paper never requires editing this file first.
//
// Scope: the sittings whose papers the examining body released publicly. CAS
// stopped publishing past papers, answer keys and examiners' reports when the
// exams moved to computer-based testing, so the shelf ends at Fall 2019 for
// Exam 5, MAS-I and MAS-II alike. Exam P and Exam FM aren't listed at all — the
// SOA publishes a rolling sample-question set rather than dated papers, so those
// exams have no sittings to browse (the browser falls back to the generated
// mock alone).
//
// Every entry is a paper that was really sat and really released — a sitting is
// transcribed from the examining body's own list, never inferred from what the
// question bank happens to hold. Bank tags are not evidence a sitting existed:
// a question moved onto an exam by a syllabus change keeps the date of the
// paper it came from, which is why `lib/pastExams.ts` builds no row from one.

export type ExamSession = 'Spring' | 'Fall'

/**
 * Per-sitting result statistics, as published by the examining body.
 *
 * Every field is optional and **unset by default**: these are real-world
 * figures, so a value here must come from the examining body's published
 * statistics (CAS "Summary of Exam Statistics") or an aggregator such as
 * Actuarial Lookup — never an estimate. A sitting with no figures renders a
 * muted "—" and the browser links out to the lookup instead of guessing.
 */
export interface PastExamStats {
  /** Candidates who sat the exam — CAS calls this "exams taken". */
  candidates?: number
  /** Raw pass ratio as a percentage, 0–100. */
  passRate?: number
  /**
   * The **effective pass ratio** as a percentage, 0–100: the raw ratio with
   * candidates who scored under half the pass mark removed, i.e. the pass rate
   * among candidates who made a serious attempt. This is the number a
   * candidate actually cares about, which is why it's the one the browser
   * leads with.
   */
  effectivePassRate?: number
}

export interface PastExamSitting extends PastExamStats {
  /** Exam topic, matching `Question.exam` (e.g. `'Exam 5'`). */
  exam: string
  year: number
  session: ExamSession
  /**
   * Questions on the real paper, when known. The bank may hold fewer (a
   * partially converted sitting), so this is the honest "how long was the real
   * thing" figure rather than the draw size.
   */
  officialQuestionCount?: number
}

/** Every sitting the browser knows about, in no particular order. */
export const PAST_EXAM_SITTINGS: PastExamSitting[] = [
  // ── CAS Exam 5 ─── released papers run Spring/Fall through Fall 2019 ──────
  { exam: 'Exam 5', year: 2019, session: 'Fall' },
  { exam: 'Exam 5', year: 2019, session: 'Spring' },
  { exam: 'Exam 5', year: 2018, session: 'Fall' },
  { exam: 'Exam 5', year: 2018, session: 'Spring' },
  { exam: 'Exam 5', year: 2017, session: 'Fall' },
  { exam: 'Exam 5', year: 2017, session: 'Spring' },
  { exam: 'Exam 5', year: 2016, session: 'Fall' },
  { exam: 'Exam 5', year: 2016, session: 'Spring' },
  { exam: 'Exam 5', year: 2015, session: 'Fall' },
  { exam: 'Exam 5', year: 2015, session: 'Spring' },
  { exam: 'Exam 5', year: 2014, session: 'Fall' },
  { exam: 'Exam 5', year: 2014, session: 'Spring' },
  { exam: 'Exam 5', year: 2013, session: 'Fall' },
  { exam: 'Exam 5', year: 2013, session: 'Spring' },
  { exam: 'Exam 5', year: 2012, session: 'Fall' },
  { exam: 'Exam 5', year: 2012, session: 'Spring' },
  { exam: 'Exam 5', year: 2011, session: 'Fall' },
  { exam: 'Exam 5', year: 2011, session: 'Spring' },

  // ── CAS Exam 6C ─── Exam 6-Canada. Released papers run from Fall 2013, the
  //    first on casact.org's list, to Fall 2019 — sat twice a year from 2014 ──
  { exam: 'Exam 6C', year: 2019, session: 'Fall' },
  { exam: 'Exam 6C', year: 2019, session: 'Spring' },
  { exam: 'Exam 6C', year: 2018, session: 'Fall' },
  { exam: 'Exam 6C', year: 2018, session: 'Spring' },
  { exam: 'Exam 6C', year: 2017, session: 'Fall' },
  { exam: 'Exam 6C', year: 2017, session: 'Spring' },
  { exam: 'Exam 6C', year: 2016, session: 'Fall' },
  { exam: 'Exam 6C', year: 2016, session: 'Spring' },
  { exam: 'Exam 6C', year: 2015, session: 'Fall' },
  { exam: 'Exam 6C', year: 2015, session: 'Spring' },
  { exam: 'Exam 6C', year: 2014, session: 'Fall' },
  { exam: 'Exam 6C', year: 2014, session: 'Spring' },
  { exam: 'Exam 6C', year: 2013, session: 'Fall' },

  // ── CAS Exam 7 ─── sat once a year, in the spring. Released papers run from
  //    May 2012, the first with an Examiner's Report, to Spring 2019. Their
  //    ERM questions now sit in the Exam 9 bank (`originally_exam`), so a
  //    sitting here holds its reserving and valuation questions ─────────────
  { exam: 'Exam 7', year: 2019, session: 'Spring' },
  { exam: 'Exam 7', year: 2018, session: 'Spring' },
  { exam: 'Exam 7', year: 2017, session: 'Spring' },
  { exam: 'Exam 7', year: 2016, session: 'Spring' },
  { exam: 'Exam 7', year: 2015, session: 'Spring' },
  { exam: 'Exam 7', year: 2014, session: 'Spring' },
  { exam: 'Exam 7', year: 2013, session: 'Spring' },
  { exam: 'Exam 7', year: 2012, session: 'Spring' },

  // ── CAS Exam 8 ─── sat once a year, in the fall. Released papers run from
  //    October 2012, the first with an Examiners' Report, to Fall 2019. Their
  //    reinsurance and catastrophe questions now sit in the Exam 9 bank
  //    (`originally_exam`), so a sitting here holds its classification and
  //    individual-risk-rating questions ────────────────────────────────────
  { exam: 'Exam 8', year: 2019, session: 'Fall' },
  { exam: 'Exam 8', year: 2018, session: 'Fall' },
  { exam: 'Exam 8', year: 2017, session: 'Fall' },
  { exam: 'Exam 8', year: 2016, session: 'Fall' },
  { exam: 'Exam 8', year: 2015, session: 'Fall' },
  { exam: 'Exam 8', year: 2014, session: 'Fall' },
  { exam: 'Exam 8', year: 2013, session: 'Fall' },
  { exam: 'Exam 8', year: 2012, session: 'Fall' },

  // ── CAS Exam 9 ─── sat once a year, in the spring. Released papers run from
  //    Spring 2013, the first casact.org lists, to Spring 2019. Their syllabus
  //    ("Financial Risk and Rate of Return") was mostly BKM's Investments and
  //    the rate-of-return papers, which today's Exam 9 dropped, so most of
  //    their questions are `off_syllabus` — on the shelf, out of quiz draws ──
  { exam: 'Exam 9', year: 2019, session: 'Spring' },
  { exam: 'Exam 9', year: 2018, session: 'Spring' },
  { exam: 'Exam 9', year: 2017, session: 'Spring' },
  { exam: 'Exam 9', year: 2016, session: 'Spring' },
  { exam: 'Exam 9', year: 2015, session: 'Spring' },
  { exam: 'Exam 9', year: 2014, session: 'Spring' },
  { exam: 'Exam 9', year: 2013, session: 'Spring' },

  // ── SOA Exam ALTAM ─── the same shape and the same list (read 2026-10-19);
  // earlier papers were LTAM's and MLC's, different exams.
  { exam: 'Exam ALTAM', year: 2026, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2025, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2025, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2024, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2024, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2023, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ALTAM', year: 2023, session: 'Spring', officialQuestionCount: 6 },

  // ── SOA Exam ASTAM ─── written answer, six questions; the SOA's Past Exams
  // and Solutions page lists every sitting from Spring 2023 (read 2026-10-19).
  // Earlier papers were STAM's, a different exam.
  { exam: 'Exam ASTAM', year: 2026, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2025, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2025, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2024, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2024, session: 'Spring', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2023, session: 'Fall', officialQuestionCount: 6 },
  { exam: 'Exam ASTAM', year: 2023, session: 'Spring', officialQuestionCount: 6 },

  // ── CAS Exam MAS-I ─── first sat Spring 2018 ──────────────────────────────
  { exam: 'Exam MAS-I', year: 2019, session: 'Fall', officialQuestionCount: 45 },
  { exam: 'Exam MAS-I', year: 2019, session: 'Spring', officialQuestionCount: 45 },
  { exam: 'Exam MAS-I', year: 2018, session: 'Fall' },
  { exam: 'Exam MAS-I', year: 2018, session: 'Spring' },

  // ── CAS Exam MAS-II ─── first sat Fall 2018, half a year after MAS-I, so
  //    there is no Spring 2018 paper: three released sittings in total ───────
  { exam: 'Exam MAS-II', year: 2019, session: 'Fall', officialQuestionCount: 42 },
  { exam: 'Exam MAS-II', year: 2019, session: 'Spring', officialQuestionCount: 42 },
  { exam: 'Exam MAS-II', year: 2018, session: 'Fall', officialQuestionCount: 42 },
]

/**
 * Where a candidate can look the exam's pass ratios up. Shown as a link in the
 * browser header, and the honest answer for every sitting whose statistics
 * aren't filled in above.
 */
export const PASS_RATE_LOOKUP: Record<string, { url: string; label: string }> = {
  'Exam 5': { url: 'https://www.actuarial-lookup.com/exams/5', label: 'Actuarial Lookup' },
  'Exam MAS-I': { url: 'https://www.actuarial-lookup.com/exams/mas-i', label: 'Actuarial Lookup' },
  'Exam MAS-II': { url: 'https://www.actuarial-lookup.com/exams/mas-ii', label: 'Actuarial Lookup' },
}

/** The catalogue's sittings for one exam. */
export function getPastExamSittings(exam: string): PastExamSitting[] {
  return PAST_EXAM_SITTINGS.filter(s => s.exam === exam)
}

/** The pass-rate lookup for one exam, if there is one. */
export function getPassRateLookup(exam: string): { url: string; label: string } | null {
  return PASS_RATE_LOOKUP[exam] ?? null
}
